import { isEqual, omit } from "lodash";
import React, { useEffect, useEffectEvent } from "react";
import {
  classMerge,
  EditorDefns,
  EditorProps,
  encodePath,
  getFromDataPath,
  isString,
} from "../../utility";
import { useArsContext } from "../contexts";
import { isObjectRecord } from "../typeGuards";
import styles from "./ArsTableCell.module.scss";
import { useArsTable } from "./ArsTableContextProvider";
import { arst_resolveTargetPath } from "./functions";
import {
  ARST_PATCH_DATA,
  ArsTableCellData,
  ArsTableCellType,
  ArsTextJustification,
} from "./interface";
import {
  isArsTableOperation,
  isArsTableSubGroupAnalysisMethod,
  isArsTableSubGroupGroupingFactor,
} from "./typeguards";

export interface ArsTableCellProps<T = string> extends EditorProps<ArsTableCellData<T>> {
  rowId?: string;
  colId?: string;
}

// FE-only display metadata that must not affect content-equality checks below
const DISPLAY_ONLY_KEYS = ["disableEditing", "fromHierarchyId"];

/**
 * Cell renderer for ArsTable scaffold cells.
 * Resolves display versus annotation view at render time and dispatches cell updates.
 *
 * @param props - Scaffold editor props
 * @param props.id - Unique scaffold-generated cell identifier
 * @param props.colId - Column identifier passed by ScaffoldCell
 * @param props.rowId - Row identifier passed by ScaffoldCell
 * @param props.value - Dual-view scaffold cell value
 * @returns ArsTable cell content
 */
export const ArsTableCell = <T,>({
  id,
  colId,
  rowId,
  value,
}: ArsTableCellProps<T>): React.ReactElement => {
  const ars = useArsContext();
  const { state, dispatch } = useArsTable<T>();

  /**
   * Check table group against state and update if groupingFactor has changed
   * @param targetDataPath DataPath for table group to check
   */
  const checkGroupingFactorChanges = useEffectEvent((targetDataPath: string) => {
    // istanbul ignore next
    if (!state) return;
    const thisTableGroup = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
      isArsTableSubGroupGroupingFactor(obj, state.settings.editorProps.isT),
    );
    /* istanbul ignore else */
    if (thisTableGroup) {
      const stateGroupingFactor = ars.state.groupingFactors[thisTableGroup.groupingFactor.id];
      if (
        stateGroupingFactor &&
        !isEqual(
          omit(thisTableGroup.groupingFactor, DISPLAY_ONLY_KEYS),
          omit(stateGroupingFactor, DISPLAY_ONLY_KEYS),
        )
      ) {
        dispatch({
          operation: ARST_PATCH_DATA,
          dataPath: `${targetDataPath}/groupingFactor`,
          value: stateGroupingFactor,
        });
      }
    }
  });

  /**
   * Check table group against state and update if analysisMethod has changed
   * @param targetDataPath DataPath for table group to check
   */
  const checkAnalysisMethodChanges = useEffectEvent((targetDataPath: string) => {
    // istanbul ignore next
    if (!state) return;
    const thisTableGroup = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
      isArsTableSubGroupAnalysisMethod(obj, state.settings.editorProps.isT),
    );
    /* istanbul ignore else */
    if (thisTableGroup) {
      const stateAnalysisMethod = ars.state.analysisMethods[thisTableGroup.analysisMethodId];
      if (
        stateAnalysisMethod &&
        !isEqual(
          omit(thisTableGroup.analysisMethod, DISPLAY_ONLY_KEYS),
          omit(stateAnalysisMethod, DISPLAY_ONLY_KEYS),
        )
      ) {
        dispatch({
          operation: ARST_PATCH_DATA,
          dataPath: `${targetDataPath}/analysisMethod`,
          value: stateAnalysisMethod,
        });
      }
    }
  });

  /**
   * Check table against state and update if operation has changed
   * @param targetDataPath DataPath for table operation to check
   */
  const checkOperationChanges = useEffectEvent((targetDataPath: string) => {
    // istanbul ignore next
    if (!state) return;
    const thisTableOperation = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
      isArsTableOperation(obj, state.settings.editorProps.isT),
    );
    /* istanbul ignore else */
    if (thisTableOperation) {
      const stateOperation = Object.values(ars.state.analysisMethods)
        .flatMap((am) => am.operations)
        .find((op) => op.id === thisTableOperation.operation.id);
      if (stateOperation && !isEqual(thisTableOperation.operation, stateOperation)) {
        dispatch({
          operation: ARST_PATCH_DATA,
          dataPath: `${targetDataPath}/operation`,
          value: stateOperation,
        });
      }
    }
  });

  /* Monitor for groupingFactor changes, on grouping factor cells */
  useEffect(() => {
    if (state?.bodyData && state?.settings && value.targetType === "groupingFactor") {
      checkGroupingFactorChanges(value.dataPath);
    }
  }, [
    ars.state.groupingFactors,
    dispatch,
    state?.bodyData,
    state?.settings,
    value.dataPath,
    value.targetType,
  ]);

  /* Monitor for groupingFactor changes on group/dataValues cells */
  useEffect(() => {
    if (
      value.dataPath.endsWith("/0") &&
      state?.bodyData &&
      state?.settings &&
      (value.targetType === "group" || value.targetType === "dataDriven")
    ) {
      const targetDataPath = arst_resolveTargetPath(value.dataPath);
      checkGroupingFactorChanges(targetDataPath);
    }
  }, [
    ars.state.groupingFactors,
    state?.bodyData,
    state?.settings,
    value.dataPath,
    value.targetType,
  ]);

  /* Monitor for analysisMethod changes, on analysis method cells */
  useEffect(() => {
    if (state?.bodyData && state?.settings && value.targetType === "analysisMethod") {
      checkAnalysisMethodChanges(value.dataPath);
    }
  }, [
    ars.state.analysisMethods,
    dispatch,
    state?.bodyData,
    state?.settings,
    value.dataPath,
    value.targetType,
  ]);

  /* Monitor for operation changes, on operation cells */
  useEffect(() => {
    if (state?.bodyData && state?.settings && value.targetType === "operation") {
      checkOperationChanges(value.dataPath);
    }
  }, [
    ars.state.analysisMethods,
    dispatch,
    state?.bodyData,
    state?.settings,
    value.dataPath,
    value.targetType,
  ]);

  if (!state || !rowId || !colId) return <></>;

  const editorProps = state.settings.editorProps;
  const Editor = editorProps.Editor;
  const isAnnotationsView = state.settings.tlfViewMode.value === "ANNOTATIONS";

  const displayContent = value.displayContent;
  const annotationContent = value.annotationContent;

  const editable =
    !isAnnotationsView &&
    !state.settings.disabled &&
    !value.disabled &&
    value.targetType !== "dataDriven" &&
    value.targetType !== "group" &&
    value.cellType !== ArsTableCellType.DataCell;

  let justification =
    value.justification ??
    // istanbul ignore next
    ArsTextJustification.Default;
  if (justification === ArsTextJustification.Default) {
    if (
      [
        ArsTableCellType.RowHeader,
        ArsTableCellType.RowGroupCell,
        ArsTableCellType.RowGroupLabel,
        ArsTableCellType.RowOperation,
      ].includes(value.cellType)
    ) {
      justification = ArsTextJustification.Left;
    } else justification = ArsTextJustification.Center;
  }

  const rowIndex = Object.keys(state.scaffoldModel?.cellData ?? {}).indexOf(rowId);
  const row = state.scaffoldModel?.cellData?.[rowId];
  const colIndex = Object.keys(row ?? {}).indexOf(colId);

  return (
    <div
      className={classMerge([
        styles.arsTableCell,
        styles[value.cellType as ArsTableCellType],
        editable ? "" : styles.disabled,
      ])}
      style={{
        paddingLeft: value.inlineIndent ? `${value.inlineIndent}em` : undefined,
      }}
    >
      <Editor
        id={id}
        decimalAlignPercent={"decimalAlignPercent" in value ? value.decimalAlignPercent : undefined}
        editable={editable}
        pending={state.changed}
        setValue={(newValue) => {
          const updates = [
            {
              dataPath: `${value.dataPath}/label`,
              value: newValue,
            },
            {
              dataPath: `scaffoldModel/cellData/${encodePath(rowId)}/${encodePath(colId)}/displayContent`,
              value: newValue,
            },
          ];
          /* istanbul ignore else */
          if (rowIndex > -1 && colIndex > -1) {
            updates.push({
              dataPath: `calculatedTableRows/${rowIndex}/arsTableCells/${colIndex}/displayContent`,
              value: newValue,
            });
          }
          dispatch({
            operation: ARST_PATCH_DATA,
            updates,
          });
        }}
        textAlignment={justification}
        value={
          isAnnotationsView
            ? editorProps.textToT(annotationContent)
            : (displayContent ??
              // istanbul ignore next
              editorProps.blankT)
        }
      />
    </div>
  );
};

ArsTableCell.displayName = "ArsTableCell";

/**
 * Create editor props for use with ArsTableCell
 *
 * @param tProps - Editor definitions for the generic type T used in the cell's displayContent and annotationContent
 * @returns - Editor definitions for ArsTableCell with cell data as the generic type
 */
export const ArsTableCellEditorProps = <T,>(
  tProps: EditorDefns<T>,
): EditorDefns<ArsTableCellData<T>> => ({
  Editor: ArsTableCell,
  blankT: {
    annotationContent: "",
    cellType: ArsTableCellType.DataCell,
    columnId: "",
    columnIndex: -1,
    dataPath: "",
    displayContent: tProps.textToT(""),
    justification: ArsTextJustification.Default,
    rowId: "",
    targetType: "data",
  },
  getTextFromT: (cellValue: ArsTableCellData<T>) => {
    const cellDisplay = cellValue.displayContent;
    return tProps.getTextFromT(cellDisplay);
  },
  isT: (value: unknown): value is ArsTableCellData<T> => {
    return (
      isObjectRecord(value) &&
      "annotationContent" in value &&
      isString(value.annotationContent) &&
      "cellType" in value &&
      Object.values(ArsTableCellType).includes(value.cellType as ArsTableCellType) &&
      "dataPath" in value &&
      isString(value.dataPath) &&
      "displayContent" in value &&
      tProps.isT(value.displayContent)
    );
  },
  joinTintoBlock: (cellValues: ArsTableCellData<T>[]) => {
    const displayContents = cellValues.map((cv) => cv.displayContent);
    return {
      annotationContent: cellValues.map((cv) => cv.annotationContent).join("\n"),
      cellType: ArsTableCellType.DataCell,
      columnId:
        cellValues[0]?.columnId ??
        // istanbul ignore next
        "",
      columnIndex:
        cellValues[0]?.columnIndex ??
        // istanbul ignore next
        -1,
      dataPath:
        cellValues[0]?.dataPath ??
        // istanbul ignore next
        "",
      displayContent: tProps.joinTintoBlock(displayContents),
      justification:
        cellValues[0]?.justification ??
        // istanbul ignore next
        ArsTextJustification.Default,
      rowId:
        cellValues[0]?.rowId ??
        // istanbul ignore next
        "",
      targetType: "data",
    };
  },
  replaceTextInT: (
    cellValue: ArsTableCellData<T>,
    oldPhrase: string,
    newPhrase: ArsTableCellData<T>,
  ) => {
    return {
      ...cellValue,
      displayContent: tProps.replaceTextInT(
        cellValue.displayContent,
        oldPhrase,
        newPhrase.displayContent,
      ),
    };
  },
  splitTintoLines: (cellValue: ArsTableCellData<T>) => {
    const displayContent = cellValue.displayContent;
    const splitDisplay = tProps.splitTintoLines(displayContent);
    return splitDisplay.map((line) => ({
      annotationContent: cellValue.annotationContent,
      cellType: ArsTableCellType.DataCell,
      columnId: cellValue.columnId,
      columnIndex: cellValue.columnIndex,
      dataPath: cellValue.dataPath,
      displayContent: line,
      justification: cellValue.justification,
      rowId: cellValue.rowId,
      targetType: "data",
    }));
  },
  textToT: (text: string) => ({
    annotationContent: "",
    cellType: ArsTableCellType.DataCell,
    columnId: "",
    columnIndex: -1,
    dataPath: "",
    displayContent: tProps.textToT(text),
    rowId: "",
    justification: ArsTextJustification.Default,
    targetType: "data",
  }),
});
